import { useCallback, useEffect, useState, type DependencyList } from 'react';
import { isAbortError } from '../api';

export type AsyncState<T> =
  | { status: 'loading'; data?: undefined; error?: undefined }
  | { status: 'success'; data: T; error?: undefined }
  | { status: 'error'; data?: undefined; error: unknown };

export type UseApiResult<T> = AsyncState<T> & { reload: () => void };

/**
 * Runs `load` whenever `deps` change (or `reload()` is called), aborting the
 * previous request so a slow response can never overwrite a newer one.
 */
export function useApi<T>(load: (signal: AbortSignal) => Promise<T>, deps: DependencyList): UseApiResult<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    load(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ status: 'success', data });
      },
      (error: unknown) => {
        if (!controller.signal.aborted && !isAbortError(error)) setState({ status: 'error', error });
      },
    );
    return () => controller.abort();
    // `load` is intentionally excluded: callers pass an inline closure and list what it reads in `deps`.
    // eslint-disable-next-line react/exhaustive-deps
  }, [...deps, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, reload };
}
